import { camelCase, startCase } from 'lodash'

import { TrafficImpactMapping } from 'types/api/attacks/details/create'
import {
  AttackCliArgs,
  AttackCommand,
  AttackCommandConfiguration,
  AttackName,
  AttackTypeCategory,
  InfraCommandArgs,
  SnakeCaseAttackArgs,
  SnakeCaseAttackCommandConfiguration
} from 'types/api/attacks/details/targeting'
import { Metadata } from 'types/metadata'

import { cleanObjKeysAndValues } from './obj'
import { capitalize } from './strings'

const CUSTOM_DISPLAY_NAME_MAPPING = {
  Ipaddresses: 'IP Addresses',
  hostnames: 'Hostnames',
  Dir: 'Root Directory',
  MS: 'Milliseconds'
}

export const ALL_ATTACK_TYPE_VALUES: AttackCommand[] = [
  'blackhole',
  'cpu',
  'disk',
  'dns',
  'io',
  'latency',
  'memory',
  'packet_loss',
  'process_exhaustion',
  'process_killer',
  'shutdown',
  'time_travel',
  'collect_certs'
]

/**
 * Return the object created from metadata where all keys are existing short & long properties of the argument
 * and the value is the metadata for that specific argument
 *
 * @param {array} metaArgs - The list of all argument detail of a specific command type
 * @returns {}
 */
function argsArraytoObject(metaArgs = []): {
  short: string
  long: string
  key: string
  infraCommandArgKey: keyof InfraCommandArgs
} {
  return metaArgs.reduce((accum, currentArg) => {
    accum[currentArg.short] = currentArg
    accum[currentArg.long] = currentArg
    accum[currentArg.key] = currentArg
    accum[currentArg.infraCommandArgKey] = currentArg
    return accum
  }, {})
}

function formatValue(value): number | string | boolean {
  if (!isNaN(parseFloat(value)) && isFinite(value)) {
    return parseFloat(value) as number
  }

  return value
}

export function cliArgsToInfraCommandArgs({
  attackCategory,
  command,
  args,
  providers,
  trafficImpactMapping,
  metadata
}: {
  attackCategory?: AttackTypeCategory
  command: AttackCommand
  args: AttackCliArgs
  metadata: Metadata
  providers?: string[]
  trafficImpactMapping?: TrafficImpactMapping
}): InfraCommandArgs {
  const argData = getMetaForDisplayableArgs([command, ...args], metadata, false)
  const commandArgs: SnakeCaseAttackArgs = {
    type: command,
    ...Object.fromEntries(
      Object.values(argData).map(({ infraCommandArgKey, value }) => [
        infraCommandArgKey,
        formatValue(value)
      ])
    )
  }

  if (attackCategory === AttackTypeCategory.Network) {
    if (trafficImpactMapping) {
      commandArgs['trafficImpactMapping'] = trafficImpactMapping
    }
    if (providers) {
      commandArgs['providers'] = providers
    }
  }

  return commandArgs
}

export interface DisplayableArgMetadata {
  name: AttackName
  value: string | number | boolean
  displayName: string
  isBoolean: boolean
  short: string
  long: string
  key: string
  infraCommandArgKey: keyof InfraCommandArgs
}

/**
 * Return the object created from combining metadata and attack args for better front end digestion
 *
 * @param {array} attackArgs - An array of args
 * @param metadata [] attack metadata list
 * @returns []
 */
export const getMetaForDisplayableArgs = (
  attackArgs: AttackCliArgs,
  metadata: Metadata,
  removeLengthArg = true
): DisplayableArgMetadata[] => {
  if (attackArgs == null || metadata == null) {
    return []
  }
  try {
    const { args: metaArgs } = metadata.find(
      (data) => data.command === attackArgs[0]
    )

    const rawArgs = attackArgs.slice(0)
    const displayObjects = []

    if (removeLengthArg) {
      // Remove the length which can be anywhere
      let lengthArgIndex
      if (rawArgs.indexOf('-l') >= 0) {
        lengthArgIndex = rawArgs.indexOf('-l')
      } else if (rawArgs.indexOf('--length') >= 0) {
        lengthArgIndex = rawArgs.indexOf('--length')
      } else {
        lengthArgIndex = -1
      }

      if (lengthArgIndex >= 0) {
        rawArgs.splice(lengthArgIndex, 2)
      }
    }

    const argsObject = argsArraytoObject(metaArgs)
    let currentArgIndex = 0
    // Since there is a bug where there is a 60 string at rawArgs[0] for some of the args
    // we should make sure that whatever is at each index reached is an argument in the current metadata
    // We create an object that holds all valid arguments, short and long
    // and at each index reached, we check first if it is a valid short or long
    // if it is then proceed as before
    // if not then step up to the next index
    while (currentArgIndex < rawArgs.length) {
      if (argsObject[rawArgs[currentArgIndex]]) {
        const metaArg = argsObject[rawArgs[currentArgIndex]]
        const isBool = metaArg && metaArg.type === 'Boolean'
        metaArg &&
          displayObjects.push({
            name: metaArg.name,
            value: isBool ? true : rawArgs[currentArgIndex + 1],
            displayName:
              CUSTOM_DISPLAY_NAME_MAPPING[metaArg.name] ?? metaArg.name,
            isBoolean: isBool,
            short: metaArg.short,
            long: metaArg.long,
            key: metaArg.key,
            infraCommandArgKey: metaArg.infraCommandArgKey
          })

        currentArgIndex = isBool ? currentArgIndex + 1 : currentArgIndex + 2
      } else {
        currentArgIndex++
      }
    }
    return displayObjects
  } catch (err) {
    return []
  }
}

export function getArgDisplayData(
  commandConfig:
    | AttackCommandConfiguration
    | SnakeCaseAttackCommandConfiguration,
  metadata: Metadata
): DisplayableArgMetadata[] {
  const command = cleanObjKeysAndValues<
    AttackCommandConfiguration | SnakeCaseAttackCommandConfiguration,
    AttackCommandConfiguration
  >(commandConfig, {
    keyTransformers: [camelCase],
    recursive: true
  })
  const args: InfraCommandArgs = command.infraCommandArgs
  const metadataEntry = metadata.find(
    (entry) =>
      // @ts-ignore Weird types coming from backend
      entry.command === command.infraCommandType ||
      entry.name === command.infraCommandType
  )

  // If attack doesn't have a type; bail
  if (!metadataEntry) {
    return []
  }

  const { args: metaArgs } = metadataEntry

  const argsObject = metaArgs.reduce((accum, currentArg) => {
    accum[currentArg.key] = currentArg
    accum[currentArg.infraCommandArgKey] = currentArg
    return accum
  }, {})
  return Object.entries(args)
    .filter(
      ([argKey]) =>
        argsObject[argKey] &&
        argKey !== 'type' &&
        argKey !== 'cli_args' &&
        argKey !== 'cliArgs'
    )
    .map(([argKey, argValue]) => {
      const metaArg = argsObject[argKey]
      const isBool = metaArg.type === 'Boolean'
      return {
        name: metaArg.name,
        value: isBool ? capitalize(argValue.toString()) : argValue.toString(),
        displayName: CUSTOM_DISPLAY_NAME_MAPPING[metaArg.name] ?? metaArg.name,
        isBoolean: isBool,
        short: metaArg.short,
        long: metaArg.long,
        key: metaArg.key,
        infraCommandArgKey: metaArg.infraCommandArgKey
      }
    })
}

export const getArgByKey = (
  key: string,
  displayableArgs: DisplayableArgMetadata[]
): DisplayableArgMetadata => displayableArgs.find((arg) => arg.key === key)

export const getFormattedAttackType = (type = ''): string => {
  switch (type) {
    case 'cpu':
    case 'CPU':
      return 'CPU'
    case 'io':
    case 'IO':
      return 'IO'
    case 'dns':
    case 'DNS':
      return 'DNS'
    default: {
      return startCase(type)
    }
  }
}
